import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismBanner,
  PrismButton,
  PrismIcon,
  PrismMessage,
  PrismTable,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { and } from 'ember-truth-helpers';
import HotelPricingAssistantDayDetailTabsDrawerOpenPricingDrawer from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/drawer/open-pricing-drawer';
import HotelPricingAssistantTableCellsTdPriceNew from 'lumen/components/hotel/pricing-assistant/table/cells/td-price-new';
import HotelPricingAssistantTableCellsTdRoom from 'lumen/components/hotel/pricing-assistant/table/cells/td-room';
import HotelPricingAssistantTableCellsTdSmartInsights from 'lumen/components/hotel/pricing-assistant/table/cells/td-smart-insights';
import type { Carate, Carecommendation, Day, Period } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER,
  type OpenPricingRoomCategoryToRIRoomTypes,
  type OpenRoomPricingFixedCategory,
  RECOMMENDATION_ACTION_STATES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import {
  isCarateForRateCode,
  selectedRoomCategory,
  translateRoomCategory,
} from 'lumen/utils/pricing-assistant/open-pricing-utils';

export interface RoomsTableRow extends CustomEmberTableRow {
  carecommendation?: Carecommendation | null;
  children?: RoomsTableRow[];
  id?: string;
  isCollapsed?: boolean;
  isParentRow?: boolean;
  isRoomCategory?: boolean;
  newPrice?: {
    newPriceInitialValue: number;
    price?: number;
  };
  room?: { roomName: string };
  roomCategory?: string;
}

interface HotelPricingAssistantDayDetailTabsTabOpenPricingSignature {
  Args: {
    date: string;
    dayModel: Day;
    isLoading: boolean;
    model: {
      periods: Period[];
    };
    ratesParams: { room: OpenPricingRoomCategoryToRIRoomTypes };
  };
}

export default class HotelPricingAssistantDayDetailTabsTabOpenPricing extends Component<HotelPricingAssistantDayDetailTabsTabOpenPricingSignature> {
  @service declare access: Services['access'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];
  @service
  declare pricingAssistantRecommendationActionPoller: Services['pricing-assistant-recommendation-action-poller'];
  @service declare toast: Services['toast'];

  @tracked expandedRoomCategoriesAfterInit: string[] = [];
  @tracked newPrices: Record<string, number | null> = {};
  @tracked selectedRoomForDrawer: string | null = null;
  @tracked selectedRowsAfterInit: RoomsTableRow[] | null = null;

  autofocusInitialized = false;
  COLUMNS = {
    ROOM: 'room',
    CURRENT_PRICE: 'currentPrice',
    NEW_PRICE: 'newPrice',
    SMART_INSIGHTS: 'smartInsights',
  };
  currentDay: string | null = null;

  // PRIMA OPEN PRICING TODO: investigate if we can get this working without the modifier/reset? - can getters handle everything?
  resetSelection = modifier((element, [date]: [string]): void => {
    if (this.currentDay !== date) {
      this.currentDay = date;
      this.selectedRowsAfterInit = null;
      const defaultExpandedRowCategory = selectedRoomCategory(
        this.args.ratesParams.room,
      );
      this.expandedRoomCategoriesAfterInit = [defaultExpandedRowCategory];
      this.newPrices = {};
      this.autofocusInitialized = false;
    }
  });

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('pricing_assistant.room'),
        valuePath: this.COLUMNS.ROOM,
        textAlign: 'left' as const,
        minWidth: 250,
        maxWidth: 350,
        tdComponent: HotelPricingAssistantTableCellsTdRoom,
      },
      {
        name: this.intl.t('pricing_assistant.current_price'),
        valuePath: this.COLUMNS.CURRENT_PRICE,
        textAlign: 'left' as const,
        minWidth: 100,
        maxWidth: 160,
      },
      {
        name: this.access.hasPricingAssistantEditRights
          ? this.intl.t('pricing_assistant.label.new_price')
          : this.intl.t('pricing_assistant.label.recommended_price'),
        valuePath: this.COLUMNS.NEW_PRICE,
        textAlign: 'left' as const,
        tdComponent: HotelPricingAssistantTableCellsTdPriceNew,
        minWidth: 145,
        maxWidth: 165,
        selectedRows: this.selectedChildRows,
      },
      {
        name: '',
        valuePath: this.COLUMNS.SMART_INSIGHTS,
        textAlign: 'right' as const,
        tdComponent: HotelPricingAssistantTableCellsTdSmartInsights,
        // eslint-disable-next-line @typescript-eslint/unbound-method
        toggleOpenPricingDrawer: this.toggleOpenPricingDrawer,
        minWidth: 115,
        width: 115,
        maxWidth: 115,
      },
    ];
  }

  get hasInvalidTableValues(): boolean {
    return this.selectedChildRows.some((row) => {
      const { price } = row[this.COLUMNS.NEW_PRICE] as { price: number };
      return price === null;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoading() {
    const isPollerRequestingData =
      this.pricingAssistantRecommendationActionPoller.datesBeingRefreshed.has(
        this.args.date,
      );

    return this.args.isLoading || isPollerRequestingData;
  }

  get periodOfSelectedDay(): Period | undefined {
    return this.args.model.periods.slice()[0];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pushingStatus() {
    const recommendationBeingPushed =
      this.periodOfSelectedDay?.ownHotelCarates.find((carate) => {
        return carate.carecommendation?.isBeingPushed;
      })?.carecommendation;

    if (recommendationBeingPushed) {
      // Bulk push
      if (recommendationBeingPushed.bulkPushId) {
        const bulkPushesStatus =
          this.pricingAssistantRecommendationActionPoller.bulkPushesStatuses[
            recommendationBeingPushed.bulkPushId
          ];
        if (bulkPushesStatus) {
          // Note: we don't use bulkPushesStatus.pushing here because recommendations don't immediately go to pushing state (they go to accepted first)
          return {
            isBeingPushed:
              bulkPushesStatus.pushed + bulkPushesStatus.failed <
              bulkPushesStatus.total,
            ...bulkPushesStatus,
          };
        }
        return {
          isBeingPushed: true,
          pushing: this.intl.t('general.missing'),
          pushed: this.intl.t('general.missing'),
          total: this.intl.t('general.missing'),
        };
      }

      // Single push
      return {
        isBeingPushed: true,
        pushing: 1,
        pushed: 0,
        total: 1,
      };
    }

    // For single push, we show successful push animation in the calendar before re-fetching the data
    // So we need to show the pushing state while the animation is running
    const recommendationPushed = this.periodOfSelectedDay?.ownHotelCarates.find(
      (carate) => {
        return carate.carecommendation?.isPushed;
      },
    )?.carecommendation;

    if (recommendationPushed?.id) {
      const isSuccessfulPushAnimationRunning =
        this.pricingAssistantRecommendationActionPoller.recommendationsWithPushAnimation.has(
          recommendationPushed.id,
        );

      // Show pushing state while the successful push animation is running
      if (isSuccessfulPushAnimationRunning) {
        return {
          isBeingPushed: true,
          pushing: 0,
          pushed: 1,
          total: 1,
        };
      }
    }

    return null;
  }

  @cached
  get rows(): RoomsTableRow[] {
    if (!this.periodOfSelectedDay) {
      return [];
    }

    const rows = Object.entries(
      this.periodOfSelectedDay?.ownCaratesGroupedByCemaCategory,
    ).map(([roomCategory, childrenCarates]): RoomsTableRow => {
      let filteredChildCarates = childrenCarates;

      if (this.access.hasPricingAssistantDynamicRatePricingFeature) {
        const primaStaticRateCode =
          this.hotelAndMarketService.hotel.primaStaticRates[0]?.rate_code || '';
        // For DRP calendar, we only show BAR carates
        filteredChildCarates = childrenCarates.filter((childCarate) =>
          isCarateForRateCode(childCarate, primaStaticRateCode),
        );
      }

      const childrenCaratesSortedPerAveragePricePriority =
        filteredChildCarates.sort(
          (roomA, roomB) =>
            roomA.averagePricePriority - roomB.averagePricePriority,
        );

      const roomCategoryHasException = filteredChildCarates.some(
        (childCarate) => childCarate?.carecommendation?.hasException,
      );

      const isCollapsed =
        !this.expandedRoomCategoriesAfterInit?.includes(roomCategory);

      return {
        // PRIMA OPEN PRICING TODO:
        // check if we want to show translated (as in the filters) or untranslated room category names.
        // right now category will be translated but children room names won't be.
        id: roomCategory.toString(),
        [this.COLUMNS.ROOM]: translateRoomCategory(this.intl, roomCategory),
        className: roomCategoryHasException ? 'table-row--exception' : '',
        children: childrenCaratesSortedPerAveragePricePriority.map(
          (childCarate, index) => {
            // eslint-disable-next-line @typescript-eslint/unbound-method, @typescript-eslint/unbound-method
            const { toggleOpenPricingDrawer, updateNewPrice } = this;
            const { formattedRate, roomName, carecommendation, id } =
              childCarate;
            const newPriceInitialValue =
              this.getNewPriceInitialValue(childCarate);

            const isBaseRoom =
              this.hotelAndMarketService.hotel.caxxxPmsSettings
                .pms_leading_room_id === roomName;
            // @ts-expect-error: Type 'null' cannot be used as an index type.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const newPrice = this.newPrices[id];

            let autofocus = false;
            if (index === 0) {
              // set focus on the first child of pre selected room
              autofocus = this.autofocusActive(roomCategory);
            }

            return {
              // @ts-expect-error: 'childCarate.id' is possibly 'null'.
              id: childCarate.id.toString(),
              carecommendation,
              carate: childCarate,
              className: childCarate?.carecommendation?.hasException
                ? 'table-row--exception'
                : '',
              [this.COLUMNS.ROOM]: {
                roomName,
                isBaseRoom,
              },
              [this.COLUMNS.CURRENT_PRICE]: formattedRate,
              [this.COLUMNS.NEW_PRICE]: {
                carateId: childCarate.id,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                price: newPrice,
                updateNewPrice,
                roomName,
                currencySymbol: this.hotelAndMarketService.hotel.currencySymbol,
                newPriceInitialValue,
                currentPrice: childCarate.value,
                date: this.currentDay,
                autofocus,
                hasUserUpdatedPrice: newPrice !== undefined,
              },
              [this.COLUMNS.SMART_INSIGHTS]: {
                toggleOpenPricingDrawer,
                isDrawerOpen: this.selectedRoomForDrawer !== null,
                roomName,
              },
            };
          },
        ),
        isCollapsed,
        isRoomCategory: true,
        roomCategory,
      };
    });

    const sortedRows = rows.sort(
      (a, b) =>
        OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
          a.roomCategory as OpenRoomPricingFixedCategory,
        ) -
        OPEN_PRICING_ROOM_CATEGORY_SORT_ORDER.indexOf(
          b.roomCategory as OpenRoomPricingFixedCategory,
        ),
    );

    return sortedRows;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedChildRowCount() {
    return this.selectedChildRows.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedChildRows() {
    return this.selectedRows.reduce(
      (acc: RoomsTableRow[], row: RoomsTableRow) => {
        if (row.isRoomCategory && row.children) {
          return acc.concat(row.children);
        }
        return acc.concat(row);
      },
      [],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedRows() {
    if (this.selectedRowsAfterInit !== null) {
      return this.findItemsByIds(this.rows, this.selectedRowsAfterInit);
    }

    // initial selection
    return this.rows.filter((row) => {
      return (
        row.roomCategory === selectedRoomCategory(this.args.ratesParams.room)
      );
    });
  }

  get sendToPmsButtonDisabled(): boolean {
    return this.selectedRows.length === 0 || this.hasInvalidTableValues;
  }

  get showErrorBanner(): boolean {
    return this.rows.some((row) => {
      return row['className'] === 'table-row--exception';
    });
  }

  addRowToSelection(
    row: RoomsTableRow,
    selection: RoomsTableRow[] = this.selectedRows,
  ): RoomsTableRow[] {
    if (selection.some((selectedRow) => selectedRow === row)) {
      return selection; // Avoid duplicates
    }
    return [...selection, row];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  autofocusActive(roomCategory: string) {
    const active =
      selectedRoomCategory(this.args.ratesParams.room) === roomCategory &&
      !this.autofocusInitialized;
    if (active) {
      this.autofocusInitialized = true;
    }
    return active;
  }

  autoSelectRowBeingEdited(room: string): void {
    const matchingChild = this.findMatchingChild(room);
    if (!matchingChild) {
      return;
    }

    const alreadySelected = this.selectedChildRows.some(
      (row: RoomsTableRow) => row.id === matchingChild.id,
    );

    if (alreadySelected) {
      return;
    }

    const parentRow = this.rows.find((row) =>
      row.children?.includes(matchingChild),
    );
    if (!parentRow) {
      return;
    }

    const siblings =
      parentRow.children?.filter(
        (child: RoomsTableRow) => child.id !== matchingChild.id,
      ) || [];

    const siblingsAllSelected = siblings.every((sibling: RoomsTableRow) =>
      this.selectedChildRows.some(
        (row: RoomsTableRow) => row.id === sibling.id,
      ),
    );

    if (siblings.length === 0) {
      // If it's the only one, we add the parent row instead
      this.selectedRowsAfterInit = this.addRowToSelection(parentRow);
    } else if (siblingsAllSelected) {
      // If it's the last one to be selected
      // we filter out the other previously selected individual siblings,
      // and add the parent row instead
      const filteredSelection = this.selectedRows.filter(
        (row) => !siblings.includes(row),
      );
      this.selectedRowsAfterInit = this.addRowToSelection(
        parentRow,
        filteredSelection,
      );
    } else {
      // Add the lone matching child row to the selection
      this.selectedRowsAfterInit = this.addRowToSelection(matchingChild);
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  findItemsByIds(rows: RoomsTableRow[], selectedRows: RoomsTableRow[] | null) {
    // get the matching row object from our getter,
    // based on our manually tracked rows in selectedRowsAfterInit
    if (!selectedRows) {
      return [];
    }

    // parents and children will already be alongside each other in the array
    // as when we are adding a last child we instead filter out the other siblings and add only the parent
    const selectedIds = new Set(selectedRows.map((row) => row.id));

    const getMatches = (row: RoomsTableRow): RoomsTableRow[] => {
      const matches = selectedIds.has(row.id) ? [row] : [];

      if (row.children) {
        for (const child of row.children) {
          matches.push(...getMatches(child));
        }
      }

      return matches;
    };

    return rows.flatMap(getMatches);
  }

  findMatchingChild(room: string): RoomsTableRow | null {
    // Helper function to find a matching child row recursively
    const findInRows = (rows: RoomsTableRow[]): RoomsTableRow | null =>
      rows.reduce<RoomsTableRow | null>((found, row) => {
        if (found) {
          return found;
        }
        if (row.children) {
          return findInRows(row.children);
        }
        const roomData = row.room;
        return roomData?.roomName === room ? row : null;
      }, null);

    return findInRows(this.rows);
  }

  getNewPriceInitialValue(carate: Carate): number | null {
    // In case of recommendation, show overwritten price if it exists, otherwise show the recommended price
    // In case of no recommendation, show the current price
    const { carecommendation } = carate;
    const initialValue = carecommendation
      ? carecommendation.priceToShow
      : carate.value;

    return initialValue;
  }

  @action
  onRowCollapseToggle(row: RoomsTableRow): void {
    const { id } = row;

    if (id) {
      const isExpanded = !row.isCollapsed;
      const expandedSet = new Set(this.expandedRoomCategoriesAfterInit);

      if (isExpanded) {
        expandedSet.add(id);
      } else {
        expandedSet.delete(id);
      }

      this.expandedRoomCategoriesAfterInit = Array.from(expandedSet);
    }
  }

  @action
  onRowSelect(rows: RoomsTableRow | RoomsTableRow[]): void {
    const arrayOfRows: RoomsTableRow[] = Array.isArray(rows) ? rows : [rows];
    const childrenOnly = arrayOfRows.flatMap((row) =>
      row.children ? row.children : [row],
    );

    // compare the already selected children to the newly selected children
    // to determine if a row is being added or removed from selection
    const rowIsBeingSelected = this.selectedChildRowCount < childrenOnly.length;

    this.selectedRowsAfterInit = arrayOfRows;

    // only run expansion logic if a row has been selected
    if (rowIsBeingSelected) {
      const allRowsSelected = this.rows.every((row) =>
        arrayOfRows.includes(row),
      );
      if (allRowsSelected && arrayOfRows.some((row) => row.isCollapsed)) {
        this.expandedRoomCategoriesAfterInit = arrayOfRows.map(
          (row) => row.roomCategory as string,
        );
      } else {
        const clickedRow = arrayOfRows[arrayOfRows.length - 1];
        // only run expansion logic if it's a parent row
        if (clickedRow?.isRoomCategory && clickedRow.isCollapsed) {
          this.expandedRoomCategoriesAfterInit = [
            ...this.expandedRoomCategoriesAfterInit,
            clickedRow.roomCategory as string,
          ];
        }
      }
    }
  }

  @action
  async sendToPms(): Promise<void> {
    // [{ carecommendation1, carate1 }, { carecommendation2, carate2 }, ...]
    const carecommendationAndCaratePairs = this.selectedChildRows.map(
      (row) => ({
        carecommendation: row['carecommendation'] as Carecommendation,
        carate: row['carate'] as Carate,
      }),
    );

    const result =
      await this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionBulkPush(
        carecommendationAndCaratePairs,
        RECOMMENDATION_ACTION_STATES.ACCEPTED,
        this.newPrices,
      );

    if (result?.isRequestSuccess) {
      // Kept toast message logic here instead of handleCreateRecommendationActionBulkPush because the method might be extended to push recommendations for multiple days and that might have different toast message
      let priceSendToastMessageTitle;
      let priceSendToastMessageText;
      const priceSendToastMessageDate =
        this.periodOfSelectedDay?.day.date.format(
          this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG,
        );
      if (this.selectedChildRowCount > 1) {
        priceSendToastMessageTitle = this.intl.t(
          'pricing_assistant.successful_price_send.title',
          {
            count: this.selectedRows.length,
          },
        );
        priceSendToastMessageText = this.intl.t(
          'pricing_assistant.successful_price_send_multiple.text',
          {
            htmlSafe: true,
            roomCategory: selectedRoomCategory(this.args.ratesParams.room),
            date: priceSendToastMessageDate,
            class: 'text-300--bold',
          },
        );
      } else {
        const selectedChildRowPrice = this.selectedChildRows[0]?.['newPrice'];

        if (selectedChildRowPrice) {
          const formattedPriceBeingPushed =
            this.hotelAndMarketService.formatRate(
              selectedChildRowPrice.price !== undefined
                ? selectedChildRowPrice.price
                : selectedChildRowPrice.newPriceInitialValue,
              true,
              false,
            );

          priceSendToastMessageTitle = this.intl.t(
            'pricing_assistant.successful_price_send.title',
            {
              count: 1,
            },
          );
          priceSendToastMessageText = this.intl.t(
            'pricing_assistant.successful_price_send.text',
            {
              htmlSafe: true,
              price: formattedPriceBeingPushed,
              date: priceSendToastMessageDate,
              class: 'text-300--bold',
            },
          );
        }
      }

      if (priceSendToastMessageText) {
        this.toast.success(priceSendToastMessageText, {
          title: priceSendToastMessageTitle,
          timeout: TOAST_TIMEOUT_LONG,
          showIcon: true,
        });
      }
    }
  }

  @action
  toggleOpenPricingDrawer(room: string | null): void {
    // clear out the selected room value if drawer is being closed
    this.selectedRoomForDrawer = room;
  }

  @action
  updateNewPrice(value: number | null, carateId: string, room: string): void {
    this.newPrices = { ...this.newPrices, [carateId]: value };

    this.autoSelectRowBeingEdited(room);
  }

  <template>
    {{#unless this.isLoading}}
      <div class="tab-open-pricing flex h-full flex-row overflow-hidden">
        {{#if this.pushingStatus.isBeingPushed}}
          <div class="flex w-full justify-center">
            <PrismMessage
              @description={{t
                "pricing_assistant.open_pricing.day_detail.pushing_message.description"
                pricesPushed=this.pushingStatus.pushed
                total=this.pushingStatus.total
              }}
              @illustration="100x100_upload"
              @title={{t
                "pricing_assistant.open_pricing.day_detail.pushing_message.title"
              }}
              class="flex justify-center"
            />
          </div>
        {{else}}
          <div
            class="tab-open-pricing__table-and-banner-container"
            {{this.resetSelection @date}}
          >
            {{#if this.showErrorBanner}}
              <div class="mr-800 mb-400 ml-800">
                <PrismBanner
                  @bannerMessage={{t
                    "pricing_assistant.failure_price_send.banner_message"
                  }}
                  @bannerStyle="critical"
                  @bannerTitle={{t
                    "pricing_assistant.failure_price_send.banner_title"
                  }}
                />
              </div>
            {{/if}}
            <div class="tab-open-pricing__table">
              <PrismTable
                @checkboxSelectionMode={{if
                  (and
                    this.access.hasPricingAssistantEditRights
                    this.access.hasPricingAssistantViewRights
                  )
                  "multiple"
                  "none"
                }}
                @columns={{this.columns}}
                @fillMode="first-column"
                @onRowCollapseToggle={{this.onRowCollapseToggle}}
                @onRowSelect={{this.onRowSelect}}
                @rowSelection={{this.selectedRows}}
                @rows={{this.rows}}
                @widthConstraint="eq-container"
              />
              {{#if this.hasInvalidTableValues}}
                <p
                  class="tab-open-pricing__table--invalid text-200--regular mt-100 flex flex-row gap-100"
                >
                  <PrismIcon
                    @color="critical"
                    @name="circle-error-filled"
                    @size="small"
                  />
                  {{t
                    "pricing_assistant.open_pricing.day_detail.error.empty_selected_row_input"
                  }}
                </p>
              {{/if}}
            </div>
          </div>
          <HotelPricingAssistantDayDetailTabsDrawerOpenPricingDrawer
            @date={{@date}}
            @dayModel={{@dayModel}}
            @periodOfSelectedDay={{this.periodOfSelectedDay}}
            @selectedRoomForDrawer={{this.selectedRoomForDrawer}}
            @toggleOpenPricingDrawer={{this.toggleOpenPricingDrawer}}
          />
        {{/if}}

      </div>
      {{#if
        (and
          this.access.hasPricingAssistantEditRights
          this.access.hasPricingAssistantViewRights
        )
      }}
        <div
          class="tab-open-pricing__footer flex items-center justify-end pr-500 pl-500"
        >
          <PrismButton
            @iconBefore="send"
            @isDisabled={{this.sendToPmsButtonDisabled}}
            @isLoading={{this.pushingStatus.isBeingPushed}}
            @label={{t
              "pricing_assistant.open_pricing.day_detail.footer.button.send_to_pms"
              count=this.selectedChildRowCount
            }}
            @onClick={{this.sendToPms}}
          />
        </div>
      {{/if}}
    {{/unless}}
  </template>
}
